import React, { useEffect, useState } from "react";
import { X, MessageCircleQuestion, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Price — Popup "Question de l'admin".
 *
 * Monté dans ClientDashboard + DriverDashboard. Interroge périodiquement
 * `/price/me/surveys/pending` et affiche un modal non-dismissable (sauf
 * via "Répondre plus tard") quand l'admin a envoyé une question (choix
 * libre ou choix multiples).
 *
 * Poll intervalle : 2 minutes (fetch sur mount puis setInterval).
 * L'app émet aussi un évènement `price:survey-refresh` depuis le click
 * handler de la push notif pour un refresh instantané.
 */
export default function PriceSurveyPopup() {
  const [survey, setSurvey] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [answerChoice, setAnswerChoice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const fetchPending = async () => {
    try {
      const { data } = await api.get("/price/me/surveys/pending");
      if (data?.survey) {
        setSurvey(data.survey);
        setDismissed(false);
      } else {
        setSurvey(null);
      }
    } catch (_e) {
      // best-effort
    }
  };

  useEffect(() => {
    fetchPending();
    const int = setInterval(fetchPending, 120_000); // 2 min
    const onRefresh = () => fetchPending();
    try {
      window.addEventListener("price:survey-refresh", onRefresh);
    } catch (_e) {/*noop*/}
    return () => {
      clearInterval(int);
      try { window.removeEventListener("price:survey-refresh", onRefresh); } catch (_e) {/*noop*/}
    };
  }, []);

  const submit = async () => {
    if (!survey) return;
    if (submitting) return;
    if (survey.type === "text" && !answerText.trim()) {
      toast.error("Écris ta réponse");
      return;
    }
    if (survey.type === "choice" && !answerChoice) {
      toast.error("Choisis une option");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/price/me/surveys/${survey.id}/respond`, {
        answer_text: survey.type === "text" ? answerText.trim() : undefined,
        answer_choice: survey.type === "choice" ? answerChoice : undefined,
      });
      toast.success("Merci pour ta réponse !");
      setSurvey(null);
      setAnswerText("");
      setAnswerChoice("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!survey || dismissed) return null;

  return (
    <div
      className="fixed inset-0 z-[85] bg-black/60 overflow-y-auto"
      data-testid="survey-popup"
    >
      <div className="min-h-full flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div className="w-full max-w-md rounded-t-3xl sm:rounded-2xl bg-[#FFF8F1] border-2 border-black shadow-[0_6px_0_0_#000] max-h-[90vh] overflow-y-auto">
          <div className="flex items-start justify-between gap-3 p-5 border-b-2 border-black/10">
            <div className="flex items-start gap-3 min-w-0">
              <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-[#FFE0CC] border-2 border-black">
                <MessageCircleQuestion className="h-5 w-5" strokeWidth={2.75} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60 leading-none">
                  Question de Price
                </div>
                <div className="mt-1 font-[Outfit] text-[16px] font-black leading-tight">
                  {survey.question}
                </div>
              </div>
            </div>
            <button
              onClick={() => setDismissed(true)}
              data-testid="survey-popup-later"
              className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-white border-2 border-black active:translate-y-[1px]"
              aria-label="Répondre plus tard"
              title="Répondre plus tard"
            >
              <X className="h-4 w-4" strokeWidth={3} />
            </button>
          </div>

          <div className="p-5 space-y-3">
            {survey.type === "text" ? (
              <textarea
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                data-testid="survey-popup-text"
                placeholder="Ta réponse…"
                rows={4}
                maxLength={2000}
                className="w-full rounded-xl bg-white border-2 border-black p-3 text-[14px] outline-none focus:ring-2 focus:ring-primary/30"
                style={{ fontSize: 16 }}
              />
            ) : (
              <div className="space-y-2">
                {(survey.choices || []).map((c) => (
                  <button
                    key={c}
                    onClick={() => setAnswerChoice(c)}
                    data-testid={`survey-popup-choice-${c}`}
                    className={`w-full text-left rounded-xl px-3 py-3 text-[14px] font-bold border-2 border-black transition-all active:translate-y-[1px] ${
                      answerChoice === c
                        ? "bg-primary text-primary-foreground shadow-[0_3px_0_0_#000]"
                        : "bg-white"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="sticky bottom-0 bg-[#FFF8F1] px-5 py-4 border-t-2 border-black/10">
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="survey-popup-submit"
              className="w-full rounded-2xl bg-primary text-primary-foreground font-black py-3.5 border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {submitting ? "Envoi…" : "Envoyer ma réponse"}
            </button>
            <div className="mt-2 text-center text-[11px] text-foreground/50">
              Ta réponse est visible uniquement par l'admin Price
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
